import { gql } from '@apollo/client';
import { AudioPlayerContextProvider, TimeSettingsContextProvider } from '@mnd-frontend/contexts';
import { getUserTimezone } from '@mnd-frontend/dates';
import { NewsroomLocale } from '@mnd-frontend/graphql-schema';
import { Alert, UIContextProvider, type UIContextValue } from '@mnd-frontend/ui';
import AbsoluteRedirect from '../../components/AbsoluteRedirect';
import PageWrapper from '../../components/PageWrapper';

import { useCheckNewNavigationDisabled, useFeatureCookie } from '@mnd-frontend/hooks';
import { ReportTranslationContextProvider } from '@mnd-frontend/reports-shared';
import type { TFunction } from 'i18next';
import React, { Suspense, useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation, useMatch, useNavigate } from 'react-router';
import { useUserLocaleAndTimezone } from '../../contexts/UserLocaleAndTimezoneContext';
import { useAppQuery } from '../../generated/hooks';
import { isDevelopment } from '../../lib/environment';
import mndUrl, { SubDomain } from '../../lib/mndUrl';
import ContactsInactivePage from '../../modules/contacts/pages/ContactList/ContactsInactivePage';
import EmailVerification from '../../modules/emailVerification/EmailVerification';
import AppContainer from '../../modules/global/components/AppContainer';
import TwoFactorEnforced from '../../modules/global/components/TwoFactorEnforced';
import PressroomSelector from '../../modules/pressroomSelector';
import { ReportUpsellPage } from '../../modules/reports/ReportUpsellPage';
import TagManagement from '../../modules/tagManagement';
import routes from '../../routes';
import { CreateBrandReportContextProvider } from '../reports/hooks/CreateBrandReportContext';
import { CypressModalHider } from './components/CypressModalHider';
import HooksIsolator from './components/HooksIsolator';
import OlarkScript from './components/OlarkScript';
import Page404 from './components/Page404';
import SalesforceChatScript from './components/SalesforceChatScript';
import { ValidateMaterialType } from './components/ValidateMaterialType';

const StoryWidget = React.lazy(() => import('../storyWidget'));
const AppMessages = React.lazy(() => import('./components/AppMessages'));
const EmailVerificationBanner = React.lazy(
  () => import('../emailVerification/EmailVerificationBanner'),
);
const MaintenanceNotificationBanner = React.lazy(
  () => import('../maintenanceNotification/MaintenanceNotificationBanner'),
);

const NewsAlertsListPage = React.lazy(() => import('../monitor/components/NewsAlertsListPage'));
const MonitorAlertsListPage = React.lazy(() => import('../monitor/pages/Alerts/ListPage'));
const MonitorAlertsCreatePage = React.lazy(() => import('../monitor/pages/Alerts/CreatePage'));
const MonitorAlertsEditPage = React.lazy(() => import('../monitor/pages/Alerts/EditPage'));
const ClipboardCreateUpdate = React.lazy(() => import('../monitor/pages/ClipboardCreateUpdate'));
const ClipboardList = React.lazy(() => import('../monitor/pages/ClipboardList'));
const ClipboardView = React.lazy(() => import('../monitor/pages/ClipboardView'));
const Insights = React.lazy(() => import('../monitor/pages/Insights'));
const NewsAlerts = React.lazy(() => import('../monitor/pages/NewsAlerts'));
const SearchProfile = React.lazy(() => import('../monitor/pages/SearchProfile'));
const MonitorStartPage = React.lazy(() => import('../monitor/pages/StartPage'));
const BrandSearchProfile = React.lazy(() => import('../monitor/pages/SearchProfile/Brand'));
const EditBrandSearchProfile = React.lazy(
  () => import('../monitor/pages/SearchProfile/Brand/edit'),
);
const BooleanSearchProfile = React.lazy(
  () => import('../monitor/pages/SearchProfile/BooleanSearchProfile'),
);
const SearchProfilesList = React.lazy(() => import('../monitor/pages/SearchProfilesList'));

const Enable2FAPage = React.lazy(() => import('../userSettings/components/Enable2FAPage'));
const ForgotPasswordPage = React.lazy(
  () => import('../userSettings/components/ForgotPasswordPage'),
);
const NewRecoveryCodesPage = React.lazy(
  () => import('../userSettings/components/NewRecoveryCodesPage'),
);
const UserSettingsPage = React.lazy(() => import('../userSettings/components/UserSettingsPage'));
const ReportsList = React.lazy(() => import('../reports/views/list'));

const Material = React.lazy(() => import('../analyze/components/DashboardMaterial'));
const MaterialList = React.lazy(() => import('../analyze/components/MaterialList'));
const AnalyzeOverview = React.lazy(() => import('../analyze/components/Overview'));
const AnalyzeNewsroom = React.lazy(() => import('../analyze/components/Newsroom'));
const DeprecatedAnalyzeNewsroom = React.lazy(
  () => import('../analyze/components/Newsroom/DeprecatedNewsroom'),
);

const ConfirmationPage = React.lazy(() => import('../publish/components/ConfirmationPage'));
const ContactPeopleListPage = React.lazy(
  () => import('../publish/components/ContactPeople/ContactPeopleListPage'),
);
const ContactPeopleEditPage = React.lazy(
  () => import('../publish/components/ContactPeople/ContactPeopleEditPage'),
);
const DistributionPage = React.lazy(
  () => import('../publish/components/Distribution/DistributionPage'),
);
const EditPage = React.lazy(() => import('../publish/components/EditMaterials/EditPage'));
const ImageBankPage = React.lazy(() => import('../publish/components/ImageBankPage'));
const ImageBankEdit = React.lazy(() => import('../publish/components/ImageBankPage/ImageBankEdit'));
const MaterialListPage = React.lazy(() => import('../publish/components/MaterialListPage'));
const MaterialListPageNewNavigation = React.lazy(
  () => import('../publish/components/MaterialListPageNewNavigation'),
);

const IdeasPage = React.lazy(() => import('../publish/components/IdeasPage'));
const AudienceBuilderPage = React.lazy(() => import('../contacts/pages/AudienceBuilderPage'));
const Contact = React.lazy(() => import('../contacts/pages/Contact'));
const ContactListPage = React.lazy(() => import('../contacts/pages/ContactList/ContactListPage'));
const ContactsAll = React.lazy(() => import('../contacts/pages/ContactList/ContactsAllPage'));
const ImportPage = React.lazy(() => import('../contacts'));
const Contacts = React.lazy(() => import('../contacts/pages/Contacts'));
const Dashboard = React.lazy(() => import('../dashboard'));
const Settings = React.lazy(() => import('../settings'));
const Unlocker = React.lazy(() => import('../unlocker'));
const Accesses = React.lazy(() => import('../accesses'));
const EmailSettings = React.lazy(() => import('../emailSettings'));
const ChannelDetailsPage = React.lazy(
  () => import('../channelSettings/components/ChannelDetailsPage'),
);
const ChannelSettingsPage = React.lazy(
  () => import('../channelSettings/components/ChannelSettingsPage'),
);
const ConnectChannelPage = React.lazy(
  () => import('../channelSettings/components/ConnectChannelPage'),
);
const AccountOverviewPage = React.lazy(() => import('../accountSettings/pages/AccountOverview'));
const CancelSubscription = React.lazy(() => import('../accountSettings/pages/CancelSubscription'));

const AccountSecurity = React.lazy(() => import('../accountSecuritySettings'));
const EmailReports = React.lazy(() => import('../emailReports/EmailReports'));

const PitchList = React.lazy(() => import('../pitch/views/list'));
const PitchEdit = React.lazy(() => import('../pitch/views/edit'));

const Reports = React.lazy(() => import('../reports'));
const ReportRoutes = React.lazy(() => import('../reports/ReportRoutes'));

const CreateBrandReportPage = React.lazy(() => import('../reports/views/create'));

const MediaRelations = React.lazy(() => import('../mediaRelations'));

const GenerateMaterialsPage = React.lazy(
  () => import('../publish/components/GenerateMaterialsPage'),
);

const PreviewPage = React.lazy(() => import('../trial/Preview'));

const CopilotSettings = React.lazy(() => import('../coPilot/settings'));

const getUiContextValue = (t: TFunction): UIContextValue => ({
  useNavigate,
  useLocation,
  useTranslation: () => ({ t }),
});

const SuspenseLoader = () => {
  if (import.meta.env.DEV) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        Please wait while vite is compiling assets...
      </div>
    );
  }
  return <div data-testid="root-suspense-loader" />;
};

gql`
  query App($pressroomId: ID!, $skipPressroom: Boolean!) {
    me {
      id
      viewAsAdmin
      user {
        id
        emailVerified
        enforceEmailVerification
        enforce2fa
        isMynewsdeskAdmin
        email
      }
    }
    pressroom(id: $pressroomId) @skip(if: $skipPressroom) {
      id
      siteLocale
      siteCode
      name
      timeZone
      newsroomSettingsPage
      copilotError
      audienceBuilderMaintenance
      newsroomSettings {
        id
        companyName
        homepageUrl
        locale
        gaProfileId
        newsroomStyle {
          ...Branding
        }
      }
      features {
        id
        analyze: managed(feature: analyze) {
          id
          enabled
        }
        analyzeLight: managed(feature: analyzeLight) {
          id
          enabled
        }
        analyzeMaterial: managed(feature: analyzeMaterial) {
          id
          enabled
        }
        monitor: managed(feature: monitor) {
          id
          enabled
          enabledWithoutAdminAccess
          previousTrial
        }
        prImpact: managed(feature: prImpact) {
          id
          enabled
        }
        coPilotIdeation: managed(feature: coPilotIdeation) {
          id
          enabled
        }
        coPilotContactCleaner: managed(feature: coPilotContactCleaner) {
          id
          enabled
        }
        mndReach
        emailReportsPage
        images
        audienceBuilder2 {
          enabled
          showUpsellPage
        }
        copilotFeatures
      }
      monitor {
        id
        hasSearchProfiles
        hasSmartSearchProfiles
      }
      source {
        id
        name
      }
    }
    maintenanceNotification {
      id
      notification {
        message
        showStatusPageLink
      }
    }
  }
`;
const isReport = /^report\./.test(window.location.hostname);

const RedirectToPublicMaterialReport = () => {
  const { pressroomId, materialType, id } = routes.analyzeMaterial.useParams();
  return (
    <AbsoluteRedirect
      href={mndUrl(
        SubDomain.Report,
        `/analyze/${materialType}/${id}?newsroomId=${pressroomId}${window.location.search.replace(
          /^\?/,
          '&',
        )}`,
      )}
    />
  );
};

const App = () => {
  const match = useMatch<'pressroomId', string>('/:pressroomId/*');
  const pressroomId = Number.isNaN(Number(match?.params.pressroomId))
    ? undefined
    : match?.params.pressroomId;
  const { t } = useTranslation();

  const { data, loading } = useAppQuery({
    variables: {
      pressroomId: pressroomId ?? '',
      skipPressroom: !pressroomId,
    },
    // eslint-disable-next-line no-console
    onError: console.error,
  });
  const { locale } = useUserLocaleAndTimezone();
  const hasSalesforceChat = useFeatureCookie('salesforce_chat');
  const newNavigationDisabled = useCheckNewNavigationDisabled();

  useEffect(() => {
    const { pressroom } = data ?? {};
    if (!loading) {
      document.title =
        [pressroom?.name, pressroom?.source?.name].filter(x => !!x).join(' | ') || 'Mynewsdesk';
    }
  }, [data, loading]);

  const uiContextValue = useMemo(() => getUiContextValue(t), [t]);
  const showImageBank = useMemo(
    () => data?.pressroom?.features?.images,
    [data?.pressroom?.features],
  );

  if (!loading && !data?.me && !isReport) {
    const currentUrl = `${window.location.pathname}${window.location.search}`;

    return (
      <AbsoluteRedirect
        href={`${mndUrl(SubDomain.WWW, '/user/signin')}${
          currentUrl === '/' ? '' : `?return_to=${currentUrl}`
        }`}
      />
    );
  }

  if (!loading && pressroomId && !data?.pressroom && !isReport) {
    // no access to this pressroom
    return <AbsoluteRedirect href={mndUrl(SubDomain.Publish, routes.pressroomSelector())} />;
  }

  const analyzeLight = Boolean(data?.pressroom?.features.analyzeLight.enabled);
  const analyzePro = Boolean(data?.pressroom?.features.analyze.enabled);
  const analyzeMaterial = Boolean(data?.pressroom?.features.analyzeMaterial.enabled);
  const audienceBuilderEnabled = Boolean(data?.pressroom?.features.audienceBuilder2?.enabled);
  const audienceBuilderUpsell = Boolean(data?.pressroom?.features.audienceBuilder2?.showUpsellPage);
  const hasContactCleaner = Boolean(data?.pressroom?.features.coPilotContactCleaner.enabled);
  const hasIdeation = Boolean(data?.pressroom?.features.coPilotIdeation.enabled);
  const pressroomLocale = data?.pressroom?.newsroomSettings?.locale || NewsroomLocale.En;
  const pressroomTimezone = data?.pressroom?.timeZone || getUserTimezone();
  const enforce2Fa = data?.me?.user?.enforce2fa === true;
  const sourceId = data?.pressroom?.source?.id;
  const isMynewsdeskAdmin = data?.me?.user.isMynewsdeskAdmin;
  const viewAsAdmin = data?.me?.viewAsAdmin;
  const emailVerified = data?.me?.user.emailVerified;
  const enforceEmailVerification = data?.me?.user.enforceEmailVerification;
  const hasSearchProfiles = data?.pressroom?.monitor?.hasSearchProfiles;
  const hasSmartSearchProfiles = data?.pressroom?.monitor?.hasSmartSearchProfiles;
  const mndReachEnabled = data?.pressroom?.features.mndReach;
  const monitorEnabled = data?.pressroom?.features.monitor.enabled === true;
  const monitorEnabledWithoutAdminAccess =
    data?.pressroom?.features.monitor.enabledWithoutAdminAccess === true;
  const monitorTrialEnded = !monitorEnabled && !!data?.pressroom?.features.monitor.previousTrial;

  const maintenanceNotification = data?.maintenanceNotification.notification;

  return (
    <ReportTranslationContextProvider t={t} lng={locale} Trans={Trans}>
      <UIContextProvider value={uiContextValue}>
        <TimeSettingsContextProvider
          userLocale={locale}
          pressroomLocale={pressroomLocale}
          pressroomTimezone={pressroomTimezone}
        >
          <AudioPlayerContextProvider>
            <AppContainer
              isMynewsdeskAdmin={isMynewsdeskAdmin}
              newNavigationDisabled={newNavigationDisabled}
              pressroomId={pressroomId || ''}
              sourceId={sourceId}
              emailVerification={enforceEmailVerification}
            >
              <HooksIsolator isMynewsdeskAdmin={isMynewsdeskAdmin} />
              {pressroomId &&
                sourceId &&
                (hasSalesforceChat ? (
                  <SalesforceChatScript />
                ) : (
                  <OlarkScript pressroomId={pressroomId} sourceId={sourceId} />
                ))}
              <Suspense fallback={<SuspenseLoader />}>
                <PageWrapper>
                  {loading ? null : enforceEmailVerification ? (
                    <EmailVerification email={data?.me?.user.email || ''} />
                  ) : (
                    <>
                      {!emailVerified && <EmailVerificationBanner pressroomId={pressroomId} />}
                      {maintenanceNotification && (
                        <MaintenanceNotificationBanner notification={maintenanceNotification} />
                      )}
                      {data?.pressroom?.copilotError && (
                        <Alert type="error"> {data.pressroom.copilotError}</Alert>
                      )}
                      <Routes>
                        <Route
                          path={routes.pressroomSelector.pattern}
                          element={<PressroomSelector />}
                        />
                        <Route path={routes.userSettings.pattern}>
                          <Route
                            path={routes.userSettingsRoot.pattern}
                            element={<UserSettingsPage />}
                          />
                          <Route
                            path={routes.userSettings2FaSetup.pattern}
                            element={<Enable2FAPage />}
                          />
                          <Route
                            path={routes.userSettingsNewCodes.pattern}
                            element={<NewRecoveryCodesPage />}
                          />
                          <Route
                            path={routes.userSettingsForgotPassword.pattern}
                            element={<ForgotPasswordPage />}
                          />
                        </Route>
                        <Route path={routes.userSettingsWithPressroom.pattern}>
                          <Route
                            path={routes.userSettingsWithPressroomRoot.pattern}
                            element={<UserSettingsPage />}
                          />
                          <Route
                            path={routes.userSettingsWithPressroom2FaSetup.pattern}
                            element={<Enable2FAPage />}
                          />
                          <Route
                            path={routes.userSettingsWithPressroomNewCodes.pattern}
                            element={<NewRecoveryCodesPage />}
                          />
                          <Route
                            path={routes.userSettingsWithPressroomForgotPassword.pattern}
                            element={<ForgotPasswordPage />}
                          />
                        </Route>
                        {pressroomId ? (
                          enforce2Fa ? (
                            <Route
                              path="*"
                              element={<TwoFactorEnforced pressroomId={pressroomId} />}
                            />
                          ) : (
                            <>
                              {/* Safe to start to use `useCurrentPressroom()`-hook on all of these routes */}

                              <Route
                                path={routes.pressroomRoot.pattern}
                                element={
                                  <Navigate to={routes.dashboard({ pressroomId })} replace />
                                }
                              />
                              <Route path={routes.preview.pattern} element={<PreviewPage />} />
                              <Route
                                path={routes.reportsCreate.pattern} /* Keep if bookmarked */
                                element={<Navigate to={routes.reports({ pressroomId })} replace />}
                              />
                              <Route
                                path={routes.reports.pattern + '/*'}
                                element={
                                  <CreateBrandReportContextProvider>
                                    <Routes>
                                      <Route index element={<Reports />} />
                                      <Route
                                        path={routes.reportsCreateBrand.pattern}
                                        element={<CreateBrandReportPage />}
                                      />
                                      <Route
                                        path={
                                          routes.reportsCreateBrandFromSimpleSearchProfile.pattern
                                        }
                                        element={<CreateBrandReportPage />}
                                      />
                                      <Route
                                        path={routes.reportsList.pattern}
                                        element={<ReportsList />}
                                      />
                                      <Route
                                        path={routes.reportsById.pattern}
                                        element={<ReportRoutes />}
                                      />
                                      <Route
                                        path={routes.reportsUpsell.pattern}
                                        element={<ReportUpsellPage />}
                                      />
                                    </Routes>
                                  </CreateBrandReportContextProvider>
                                }
                              ></Route>
                              <Route path={routes.pitchList.pattern}>
                                <Route index element={<PitchList />} />
                                <Route path={routes.pitchEdit.pattern} element={<PitchEdit />} />
                                <Route path={routes.pitchCreate.pattern} element={<PitchEdit />} />
                              </Route>
                              <Route path={routes.dashboard.pattern} element={<Dashboard />} />
                              <Route
                                path={routes.tagManagement.pattern}
                                element={<TagManagement />}
                              />
                              <Route path={routes.storyWidget.pattern} element={<StoryWidget />}>
                                <Route path={routes.storyWidgetTab.pattern} element={null} />
                              </Route>
                              <Route
                                path={routes.analyze.pattern}
                                element={
                                  <Navigate
                                    to={
                                      analyzeLight
                                        ? routes.analyzeOverview({ pressroomId })
                                        : routes.analyzeNewsroom({ pressroomId })
                                    }
                                  />
                                }
                              />
                              <Route
                                path={routes.analyzeOverview.pattern}
                                element={
                                  analyzeLight ? <AnalyzeOverview /> : <Unlocker id="analyze" />
                                }
                              />
                              <Route
                                path={routes.analyzeNewsroom.pattern}
                                element={
                                  isReport ? (
                                    <AbsoluteRedirect
                                      href={mndUrl(
                                        SubDomain.Report,
                                        `/analyze/newsroom/${pressroomId}${window.location.search}`,
                                      )}
                                    />
                                  ) : analyzePro ? (
                                    <AnalyzeNewsroom />
                                  ) : (
                                    <Unlocker id="analyze" />
                                  )
                                }
                              />
                              <Route
                                path={routes.analyzeUpgrade.pattern}
                                element={<Unlocker id="analyze" />}
                              />
                              <Route
                                path={routes.analyzeNewsroomDeprecated.pattern}
                                element={<DeprecatedAnalyzeNewsroom />}
                              >
                                <Route
                                  path={routes.analyzeNewsroomDeprecated2.pattern}
                                  element={null}
                                />
                                <Route
                                  path={routes.analyzeNewsroomDeprecated3.pattern}
                                  element={null}
                                />
                              </Route>
                              <Route
                                path={routes.analyzeMaterial.pattern}
                                element={
                                  isReport ? (
                                    <RedirectToPublicMaterialReport />
                                  ) : analyzeMaterial ? (
                                    <ValidateMaterialType includeImages>
                                      <Material />
                                    </ValidateMaterialType>
                                  ) : (
                                    <Unlocker id="analyze" />
                                  )
                                }
                              >
                                <Route path={routes.analyzeMaterialTab.pattern} element={null} />
                              </Route>
                              <Route
                                path={routes.analyzeMaterialList.pattern}
                                element={
                                  analyzePro ? (
                                    <ValidateMaterialType
                                      renderChildrenOnMissingParam
                                      includeImages
                                    >
                                      <MaterialList />
                                    </ValidateMaterialType>
                                  ) : (
                                    <Unlocker id="analyze" />
                                  )
                                }
                              >
                                <Route
                                  path={routes.analyzeMaterialListOfType.pattern}
                                  element={null}
                                />
                              </Route>
                              <Route
                                path={routes.monitor.pattern}
                                element={
                                  monitorEnabled ? undefined : (
                                    <MonitorStartPage
                                      hasMonitor={monitorEnabled}
                                      newNavigationDisabled={newNavigationDisabled}
                                      trialEnded={monitorTrialEnded}
                                      viewAsAdmin={viewAsAdmin}
                                    />
                                  )
                                }
                              >
                                <Route
                                  index
                                  element={
                                    hasSearchProfiles ? (
                                      <Navigate
                                        to={routes.monitorInsights({ pressroomId })}
                                        replace
                                      />
                                    ) : (
                                      <Navigate
                                        to={routes.monitorStartPage({ pressroomId })}
                                        replace
                                      />
                                    )
                                  }
                                />
                                <Route
                                  path={routes.monitorUpgrade.pattern}
                                  element={<Unlocker id="prime" />}
                                />
                                <Route
                                  path={routes.monitorInsights.pattern}
                                  element={
                                    <Insights
                                      monitorEnabledWithoutAdminAccess={
                                        monitorEnabledWithoutAdminAccess
                                      }
                                      viewAsAdmin={!!viewAsAdmin}
                                    />
                                  }
                                >
                                  <Route path={routes.monitorInsightsTab.pattern} element={null} />
                                </Route>
                                <Route
                                  path={routes.monitorClipboardCreate.pattern}
                                  element={<ClipboardCreateUpdate />}
                                >
                                  <Route
                                    path={routes.monitorClipboardEdit.pattern}
                                    element={null}
                                  />
                                </Route>
                                <Route
                                  path={routes.monitorClipboardShow.pattern}
                                  element={<ClipboardView />}
                                />
                                <Route
                                  path={routes.monitorClipboardList.pattern}
                                  element={<ClipboardList />}
                                />
                                <Route
                                  path={routes.monitorSearchProfilesCreateUntyped.pattern}
                                  element={<SearchProfile />}
                                >
                                  <Route
                                    path={routes.monitorSearchProfilesEditUntyped.pattern}
                                    element={null}
                                  />
                                </Route>
                                <Route
                                  path={routes.monitorStartPage.pattern}
                                  element={
                                    <MonitorStartPage
                                      newNavigationDisabled={newNavigationDisabled}
                                      hasMonitor={monitorEnabled}
                                      trialEnded={monitorTrialEnded}
                                    />
                                  }
                                ></Route>
                                <Route
                                  path={routes.monitorSearchProfilesCreateBrand.pattern}
                                  element={
                                    <BrandSearchProfile
                                      isOnboarding={false}
                                      viewAsAdmin={viewAsAdmin}
                                    />
                                  }
                                />
                                <Route
                                  path={routes.monitorSearchProfilesOnboarding.pattern}
                                  element={
                                    <BrandSearchProfile
                                      isOnboarding={true}
                                      viewAsAdmin={viewAsAdmin}
                                    />
                                  }
                                ></Route>
                                <Route
                                  path={routes.monitorSearchProfilesEditBrand.pattern}
                                  element={<EditBrandSearchProfile />}
                                ></Route>
                                <Route
                                  path={routes.monitorSearchProfiles.pattern}
                                  element={<SearchProfilesList />}
                                >
                                  <Route
                                    path={routes.monitorSearchProfilesCreateBoolean.pattern}
                                    element={
                                      <Suspense fallback={null}>
                                        <BooleanSearchProfile />
                                      </Suspense>
                                    }
                                  >
                                    <Route
                                      path={routes.monitorSearchProfilesEditBoolean.pattern}
                                      element={null}
                                    />
                                  </Route>
                                </Route>
                                <Route
                                  path={routes.monitorNewsAlertsCreate.pattern}
                                  element={<NewsAlerts />}
                                >
                                  <Route
                                    path={routes.monitorNewsAlertsEdit.pattern}
                                    element={null}
                                  />
                                </Route>
                                <Route
                                  path={routes.monitorNewsAlertsList.pattern}
                                  element={<NewsAlertsListPage />}
                                />
                                <Route
                                  path={routes.monitorAlertsList.pattern}
                                  element={<MonitorAlertsListPage />}
                                />
                                <Route
                                  path={routes.monitorAlertsCreate.pattern}
                                  element={<MonitorAlertsCreatePage />}
                                />
                                <Route
                                  path={routes.monitorAlertsEdit.pattern}
                                  element={<MonitorAlertsEditPage />}
                                />
                              </Route>
                              {!newNavigationDisabled ? (
                                <Route
                                  path={routes.publish.pattern}
                                  element={<MaterialListPageNewNavigation />}
                                />
                              ) : (
                                <Route
                                  path={routes.publish.pattern}
                                  element={
                                    <Navigate
                                      to={routes.publishList({
                                        pressroomId,
                                        materialType: 'pressreleases',
                                      })}
                                      replace
                                    />
                                  }
                                />
                              )}
                              <Route
                                path={routes.publishList.pattern}
                                element={
                                  <ValidateMaterialType>
                                    {newNavigationDisabled ? (
                                      <MaterialListPage
                                        showImageBank={showImageBank}
                                        email={data?.me?.user.email}
                                      />
                                    ) : (
                                      <MaterialListPageNewNavigation
                                        showImageBank={showImageBank}
                                        email={data?.me?.user.email}
                                      />
                                    )}
                                  </ValidateMaterialType>
                                }
                              />
                              <Route
                                path={routes.publishContactPeopleList.pattern}
                                element={<ContactPeopleListPage />}
                              />
                              <Route
                                path={routes.publishImagesList.pattern}
                                element={<ImageBankPage showImageBank={showImageBank} />}
                              >
                                <Route
                                  path={routes.publishImagesEdit.pattern}
                                  element={
                                    <Suspense fallback={null}>
                                      <ImageBankEdit />
                                    </Suspense>
                                  }
                                />
                              </Route>
                              <Route
                                path={routes.publishContactPeopleEdit.pattern}
                                element={<ContactPeopleEditPage />}
                              />
                              <Route
                                path={routes.publishContactPeopleCreate.pattern}
                                element={<ContactPeopleEditPage />}
                              />
                              <Route>
                                <>
                                  <Route
                                    path={routes.publishCreate.pattern + '/*'}
                                    element={
                                      <ValidateMaterialType>
                                        <EditPage />
                                      </ValidateMaterialType>
                                    }
                                  />
                                  <Route
                                    path={routes.publishEdit.pattern + '/*'}
                                    element={
                                      <ValidateMaterialType>
                                        <EditPage />
                                      </ValidateMaterialType>
                                    }
                                  />
                                </>
                              </Route>

                              <Route
                                path={routes.publishGenerate.pattern}
                                element={<GenerateMaterialsPage />}
                              />
                              <Route
                                path={routes.publishGenerateWithId.pattern}
                                element={<GenerateMaterialsPage />}
                              />
                              <Route
                                path={routes.publishDistribute.pattern}
                                element={
                                  <ValidateMaterialType>
                                    <DistributionPage />
                                  </ValidateMaterialType>
                                }
                              />
                              <Route
                                path={routes.publishConfirmation.pattern}
                                element={
                                  <ValidateMaterialType>
                                    <ConfirmationPage />
                                  </ValidateMaterialType>
                                }
                              />
                              {hasIdeation && !newNavigationDisabled ? (
                                <Route path={routes.publishIdeas.pattern} element={<IdeasPage />} />
                              ) : (
                                <Route
                                  path={routes.publishIdeas.pattern}
                                  element={
                                    <Navigate
                                      to={routes.publishList({
                                        pressroomId,
                                        materialType: 'pressreleases',
                                      })}
                                      replace
                                    />
                                  }
                                />
                              )}
                              <Route
                                path={routes.accountUserManagement.pattern}
                                element={<Accesses />}
                              />
                              <Route path={routes.channels.pattern}>
                                <Route index element={<ChannelSettingsPage />} />
                                <Route
                                  path={routes.channelsConnect.pattern}
                                  element={<ConnectChannelPage />}
                                />
                                <Route
                                  path={routes.channelsEdit.pattern}
                                  element={<ChannelDetailsPage />}
                                />
                              </Route>
                              <Route
                                path={routes.emailSettings.pattern}
                                element={<EmailSettings />}
                              >
                                <Route path={routes.emailSettingsTab.pattern} element={null} />
                              </Route>
                              <Route path={routes.contacts.pattern}>
                                <Route index element={<Contacts />} />
                                <Route path={routes.contactShow.pattern} element={<Contact />} />
                                <Route
                                  path={routes.contactsAll.pattern}
                                  element={<ContactsAll />}
                                />
                                {/* This feature will be:
                              - tested by all customers who have Sidebar feature on
                              - launched together with Sidebar feature
                            */}
                                {hasContactCleaner && (
                                  <Route
                                    path={routes.contactsInactive.pattern}
                                    element={<ContactsInactivePage />}
                                  />
                                )}

                                <Route path={routes.contactImport.pattern} element={<ImportPage />}>
                                  <Route path={routes.contactImportTab.pattern} element={null} />
                                </Route>
                                {mndReachEnabled && (
                                  <Route
                                    path={routes.contactAudienceBuilderMndReach.pattern}
                                    element={<MediaRelations />}
                                  />
                                )}
                                {(audienceBuilderEnabled || audienceBuilderUpsell) && (
                                  <Route
                                    path={routes.contactAudienceBuilder.pattern}
                                    element={
                                      audienceBuilderEnabled ? (
                                        <AudienceBuilderPage />
                                      ) : (
                                        <Unlocker id="audience_builder" />
                                      )
                                    }
                                  />
                                )}
                                <Route
                                  path={routes.contactListShow.pattern}
                                  element={<ContactListPage />}
                                />
                              </Route>
                              <Route
                                path={routes.accountSettingsCancelSubscription.pattern}
                                element={<CancelSubscription />}
                              />
                              <Route
                                path={routes.accountSettings.pattern}
                                element={<AccountOverviewPage />}
                              />
                              {data?.pressroom?.newsroomSettingsPage && (
                                <>
                                  <Route
                                    path={routes.newsroomSettings.pattern}
                                    element={<Settings />}
                                  />
                                  <Route
                                    path={routes.newsroomSettingsTab.pattern}
                                    element={<Settings />}
                                  />
                                </>
                              )}
                              <Route
                                path={routes.emailReports.pattern}
                                element={
                                  data?.pressroom?.features?.emailReportsPage ? (
                                    <EmailReports />
                                  ) : (
                                    <Page404 />
                                  )
                                }
                              />
                              <Route
                                path={routes.accountSecurity.pattern}
                                element={<AccountSecurity />}
                              />
                              {data?.pressroom?.features?.copilotFeatures && (
                                <Route
                                  path={routes.copilotSettings.pattern}
                                  element={<CopilotSettings pressroomId={pressroomId} />}
                                />
                              )}
                            </>
                          )
                        ) : (
                          <Route
                            path="*"
                            element={<Navigate to={routes.pressroomSelector()} replace />}
                          />
                        )}
                        <Route path="*" element={<Page404 />} />
                      </Routes>
                    </>
                  )}
                </PageWrapper>
                {!enforceEmailVerification && (
                  <AppMessages pressroomId={pressroomId} email={data?.me?.user.email} />
                )}
                {isDevelopment && window.Cypress && <CypressModalHider />}
              </Suspense>
            </AppContainer>
          </AudioPlayerContextProvider>
        </TimeSettingsContextProvider>
      </UIContextProvider>
    </ReportTranslationContextProvider>
  );
};

export default React.memo(App);
